import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Upload, Trash2, Loader2, FileText, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { api, API } from "../../lib/api";

const token = () => localStorage.getItem("ah_token") || "";

function fmtDT(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function monthLabel(m) {
  if (!m) return "";
  const d = new Date(`${m}-01T00:00:00`);
  const s = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function InstallerPayslips({ workers }) {
  const [rows, setRows] = useState(null);
  const [workerFilter, setWorkerFilter] = useState("");
  const [form, setForm] = useState({ worker_id: "", month: new Date().toLocaleDateString("sv-SE").slice(0, 7) });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = workerFilter ? { worker_id: workerFilter } : {};
      const { data } = await api.get("/installers/payslips", { params });
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento buste paga");
      setRows([]);
    }
  }, [workerFilter]);

  useEffect(() => { load(); }, [load]);

  async function upload(e) {
    e.preventDefault();
    if (!form.worker_id) { toast.error("Seleziona l'operaio"); return; }
    if (!file) { toast.error("Seleziona il PDF della busta paga"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("worker_id", form.worker_id);
      fd.append("month", form.month);
      await api.post("/installers/payslips", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Busta paga caricata: l'operaio riceve la notifica e deve confermare la ricezione per aprirla");
      setFile(null);
      document.getElementById("payslip-file-input").value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento busta paga");
    }
    setUploading(false);
  }

  async function del(p) {
    if (!window.confirm(`Eliminare la busta paga di ${p.worker_name} — ${monthLabel(p.month)}? Le conferme già registrate restano archiviate.`)) return;
    try {
      await api.delete(`/installers/payslips/${p.id}`);
      toast.success("Busta paga eliminata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  }

  return (
    <div className="space-y-5" data-testid="installers-payslips">
      <form onSubmit={upload} className="bg-white border border-ink/10 rounded-sm p-4 flex items-end gap-3 flex-wrap" data-testid="payslip-upload-form">
        <div className="flex items-center gap-2 mr-2">
          <Receipt size={16} className="text-ink" />
          <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-ink">Carica busta paga</span>
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.18em] uppercase text-slate1 mb-1">Operaio *</label>
          <select value={form.worker_id} onChange={(e) => setForm({ ...form, worker_id: e.target.value })} className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm min-w-[180px]" data-testid="payslip-form-worker">
            <option value="">Seleziona operaio…</option>
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.18em] uppercase text-slate1 mb-1">Mese *</label>
          <input type="month" required value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm" data-testid="payslip-form-month" />
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.18em] uppercase text-slate1 mb-1">PDF *</label>
          <input id="payslip-file-input" type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="border border-ink/20 px-3 py-1.5 rounded-sm bg-white text-xs max-w-[240px]" data-testid="payslip-form-file" />
        </div>
        <button type="submit" disabled={uploading} className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm font-bold disabled:opacity-50" data-testid="payslip-form-submit">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Carica
        </button>
        <div className="w-full text-[10px] text-slate1">Se esiste già una busta per lo stesso operaio e mese viene sostituita. L&apos;operaio può aprirla solo dopo aver confermato la ricezione (conferma archiviata con data, ora e dispositivo).</div>
      </form>

      <div className="flex items-center gap-3">
        <select value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)} className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm" data-testid="payslip-filter-worker">
          <option value="">Tutti gli operai</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        {rows && <span className="text-[11px] text-slate1">{rows.length} bust{rows.length === 1 ? "a" : "e"} paga</span>}
      </div>

      {!rows ? (
        <div className="flex justify-center py-12 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="payslips-empty">Nessuna busta paga caricata.</div>
      ) : (
        <div className="bg-white border border-ink/10 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-slate1 border-b border-ink/10">
                <th className="px-4 py-2.5">Mese</th>
                <th className="px-4 py-2.5">Operaio</th>
                <th className="px-4 py-2.5">File</th>
                <th className="px-4 py-2.5">Caricata il</th>
                <th className="px-4 py-2.5">Ricezione</th>
                <th className="px-4 py-2.5 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-ink/5" data-testid={`payslip-row-${p.id}`}>
                  <td className="px-4 py-2.5 font-medium text-ink whitespace-nowrap">{monthLabel(p.month)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{p.worker_name}</td>
                  <td className="px-4 py-2.5 text-xs text-slate1 max-w-[220px] truncate">{p.filename}</td>
                  <td className="px-4 py-2.5 text-xs text-slate1 whitespace-nowrap">{fmtDT(p.uploaded_at)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {p.signed_at ? (
                      <span className="inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" title={`Ricezione confermata il ${fmtDT(p.signed_at)}`} data-testid={`payslip-signed-${p.id}`}>
                        <CheckCircle2 size={11} /> Confermata {fmtDT(p.signed_at)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`payslip-unsigned-${p.id}`}>
                        <Clock size={11} /> In attesa di conferma
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <a href={`${API}/installers/payslips/${p.id}/content?token=${token()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 p-2 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Apri PDF" data-testid={`payslip-open-${p.id}`}>
                      <FileText size={15} /><ExternalLink size={10} />
                    </a>
                    <button onClick={() => del(p)} className="p-2 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" title="Elimina" data-testid={`payslip-del-${p.id}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
